import { supabase } from '../supabase/client';
import { bereinigeSuchbegriff } from './personenSuche';
import { eigentuemerAnzeigename } from '../eigentuemer/anzeige';
import { ohneArchivierte } from '../team/archiv';

// EINE Kontaktsuche für alle Auswahlfelder (Mail-Empfänger, Termin-Teilnehmer, …).
// Vorher hatte jede Maske ihre eigene Abfrage mit eigener Quellenliste — im Termin fehlten
// „Sonstige Kontakte" und Mitarbeitende komplett, die eigene Kollegin war dort schlicht nicht
// auffindbar (User-Meldung 2026-08-11). Wer ein neues Auswahlfeld baut, nutzt diese Funktion.

export type EmpfaengerQuelle = 'Mieter' | 'Eigentümer' | 'Dienstleister' | 'Kontakt' | 'Team';

export interface EmpfaengerTreffer {
  id: string;
  email: string;
  name: string;
  /** Herkunft, dient in den Masken als Beschriftung. */
  typ: EmpfaengerQuelle;
}

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

// ── Reine Abbildungen (testbar ohne Datenbank) ───────────────────────────────

export function mieterTreffer(m: any): EmpfaengerTreffer | null {
  const email = text(m?.email);
  if (!email) return null;
  return { id: m.id, email, name: text(m.firma_name) || `${text(m.vorname)} ${text(m.nachname)}`.trim(), typ: 'Mieter' };
}

export function eigentuemerTreffer(e: any): EmpfaengerTreffer | null {
  const email = text(e?.email);
  if (!email) return null;
  const name = eigentuemerAnzeigename(e);
  return { id: e.id, email, name: name === '—' ? email : name, typ: 'Eigentümer' };
}

export function dienstleisterTreffer(d: any): EmpfaengerTreffer | null {
  const email = text(d?.email);
  if (!email) return null;
  return { id: d.id, email, name: text(d.name) || text(d.ansprechpartner) || email, typ: 'Dienstleister' };
}

export function kontaktTreffer(k: any): EmpfaengerTreffer | null {
  const email = text(k?.email);
  if (!email) return null;
  return { id: k.id, email, name: text(k.firma_name) || `${text(k.vorname)} ${text(k.nachname)}`.trim() || email, typ: 'Kontakt' };
}

export function profilTreffer(p: any): EmpfaengerTreffer | null {
  const email = text(p?.email);
  if (!email) return null;
  return { id: p.id, email, name: text(p.full_name) || email, typ: 'Team' };
}

/** Gleiche Adresse aus mehreren Quellen nur einmal anbieten (erste gewinnt). */
export function entdopple(treffer: EmpfaengerTreffer[]): EmpfaengerTreffer[] {
  const gesehen = new Set<string>();
  const raus: EmpfaengerTreffer[] = [];
  for (const t of treffer) {
    const k = t.email.toLowerCase();
    if (gesehen.has(k)) continue;
    gesehen.add(k);
    raus.push(t);
  }
  return raus;
}

// ── Suche ────────────────────────────────────────────────────────────────────

/**
 * Sucht in ALLEN Personenquellen (Mieter, Eigentümer, Dienstleister, sonstige Kontakte,
 * Mitarbeitende) über Name, Firma und E-Mail. Ohne Treffer: leere Liste.
 */
export async function sucheEmpfaenger(query: string, proQuelle = 5): Promise<EmpfaengerTreffer[]> {
  const q = bereinigeSuchbegriff(query);
  if (q.length < 2) return [];
  const like = `%${q}%`;

  const [mieter, eigentuemer, dienstleister, kontakte, profile] = await Promise.all([
    supabase.from('mieter').select('id, vorname, nachname, firma_name, email')
      .or(`email.ilike.${like},vorname.ilike.${like},nachname.ilike.${like},firma_name.ilike.${like}`).limit(proQuelle),
    supabase.from('eigentuemer').select('id, anrede, vorname, nachname, firma_name, email')
      .or(`email.ilike.${like},vorname.ilike.${like},nachname.ilike.${like},firma_name.ilike.${like}`).limit(proQuelle),
    supabase.from('dienstleister').select('id, name, ansprechpartner, email')
      .or(`email.ilike.${like},name.ilike.${like},ansprechpartner.ilike.${like}`).limit(proQuelle),
    supabase.from('kontakte').select('id, vorname, nachname, firma_name, email')
      .or(`email.ilike.${like},vorname.ilike.${like},nachname.ilike.${like},firma_name.ilike.${like}`).limit(proQuelle),
    supabase.from('profiles').select('id, full_name, email, status')
      .or(`email.ilike.${like},full_name.ilike.${like}`).limit(proQuelle),
  ]);

  const treffer = [
    ...((mieter.data as any[]) || []).map(mieterTreffer),
    ...((eigentuemer.data as any[]) || []).map(eigentuemerTreffer),
    ...((dienstleister.data as any[]) || []).map(dienstleisterTreffer),
    ...((kontakte.data as any[]) || []).map(kontaktTreffer),
    ...ohneArchivierte((profile.data as any[]) || []).map(profilTreffer),
  ].filter((t): t is EmpfaengerTreffer => t !== null);

  return entdopple(treffer);
}
